import React, { useEffect } from "react"
import { Error403, Error404 } from "./shared/components"
import { useLocation } from "react-router-dom"
import { APIError } from "./shared/types"

const codeToComponent: {
  [key: string]: React.ComponentType | null
} = {
  "401": Error403,
  "403": Error403,
  "404": Error404,
}

type CommonErrorBoundaryProps = {
  children: React.ReactNode
}

type CommonErrorBoundaryState = {
  errorCode: string | null
}

type CaughtError = APIError | Error | undefined

export class CommonErrorBoundary extends React.Component<
  CommonErrorBoundaryProps,
  CommonErrorBoundaryState
> {
  constructor(props: CommonErrorBoundaryProps) {
    super(props)
    this.state = { errorCode: null }
  }

  static getDerivedStateFromError(error: CaughtError) {
    return { errorCode: codeFromError(error) }
  }

  componentDidCatch(error: Error) {
    if (!this.state.errorCode || !codeToComponent[this.state.errorCode])
      throw error
  }

  resetError = () => this.setState({ errorCode: null })

  render() {
    const ErrorComponent = this.state.errorCode
      ? codeToComponent[this.state.errorCode]
      : null
    if (ErrorComponent)
      return (
        <>
          <LocationWatcher resetError={this.resetError.bind(this)} />
          <ErrorComponent />
        </>
      )

    return this.props.children
  }
}

function LocationWatcher({ resetError }: { resetError: () => void }) {
  const location = useLocation()
  useEffect(resetError, [location, resetError])
  return null
}

// a function intended to be used as the retry function on a react-query query
export function unlessCommonError(i: number, error: CaughtError) {
  const statusCode = codeFromError(error)
  const isCommonErrorCode = statusCode && statusCode in codeToComponent
  const lessThanDefaultRetryCount = i < 3
  return !isCommonErrorCode && lessThanDefaultRetryCount
}

function codeFromError(error: CaughtError): string | undefined {
  if (error && "errors" in error && Array.isArray(error.errors)) {
    return (error as APIError).errors[0].status
  }
  return undefined
}
